// src/components/Logados.js
import React, { useState, useEffect } from 'react';
import { FaUser, FaClock, FaNetworkWired, FaSignInAlt, FaSignOutAlt, FaUsers, FaEye, FaChartLine } from 'react-icons/fa';
import '../styles/Logados.css';

const RegistroAcessos = () => {
  const [activeTab, setActiveTab] = useState('acessos');
  const [acessos, setAcessos] = useState([]);
  const [usuariosOnline, setUsuariosOnline] = useState([]);
  const [estatisticas, setEstatisticas] = useState({});
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtros, setFiltros] = useState({
    username: '',
    status: ''
  });

  useEffect(() => {
    carregarDados();
    
    // Atualizar dados a cada 30 segundos
    const interval = setInterval(carregarDados, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeTab === 'online') {
      carregarUsuariosOnline();
    } else if (activeTab === 'estatisticas') {
      carregarEstatisticas();
    }
  }, [activeTab]);

  const carregarDados = async () => {
    try {
      const accessToken = localStorage.getItem('access');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/api/listar-acessos/`, 
        {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        }
      );

      if (!response.ok) {
        throw new Error('Falha ao buscar registros de acesso');
      }

      const data = await response.json();
      setAcessos(data);
      setErro(null);
    } catch (error) {
      console.error('Erro:', error);
      setErro(error.message);
    } finally {
      setLoading(false);
    }
  };

  const carregarUsuariosOnline = async () => {
    try {
      const accessToken = localStorage.getItem('access');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/api/usuarios-online-agora/`, 
        {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUsuariosOnline(data.usuarios_online || []);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários online:', error);
    }
  };

  const carregarEstatisticas = async () => {
    try {
      const accessToken = localStorage.getItem('access');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/api/estatisticas-acessos/`, 
        {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setEstatisticas(data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const filtrarAcessos = () => {
    return acessos.filter(acesso => {
      const matchUsername = !filtros.username || 
        acesso.username.toLowerCase().includes(filtros.username.toLowerCase()) ||
        (acesso.first_name && acesso.first_name.toLowerCase().includes(filtros.username.toLowerCase()));
      
      const matchStatus = !filtros.status || 
        (filtros.status === 'ativo' && acesso.status === 'ativa') ||
        (filtros.status === 'finalizado' && acesso.status === 'finalizada');
      
      return matchUsername && matchStatus;
    });
  };

  const acessosFiltrados = filtrarAcessos();

  if (loading) {
    return (
      <div className="container text-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
        <p className="mt-2">Carregando registros de acesso...</p>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h5>Erro ao carregar dados</h5>
          <p>{erro}</p>
          <button className="btn btn-outline-danger" onClick={carregarDados}>
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="logados-page">
      <div className="container mt-5 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <FaUsers className="header-icon me-3" />
            <h2 className="titulo-pagina m-0">Controle de Usuários</h2>
          </div>
          
          <button 
            className="btn btn-outline-primary"
            onClick={carregarDados}
            title="Atualizar dados"
          >
            <i className="fas fa-sync-alt me-2"></i>
            Atualizar
          </button>
        </div>

        {/* Tabs de navegação */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'acessos' ? 'active' : ''}`}
              onClick={() => setActiveTab('acessos')}
            >
              <FaSignInAlt className="me-2" />
              Registro de Acessos ({acessosFiltrados.length})
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'online' ? 'active' : ''}`}
              onClick={() => setActiveTab('online')}
            >
              <FaEye className="me-2" />
              Usuários Online ({usuariosOnline.length})
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'estatisticas' ? 'active' : ''}`}
              onClick={() => setActiveTab('estatisticas')}
            >
              <FaChartLine className="me-2" />
              Estatísticas
            </button>
          </li>
        </ul>

        {/* Tab Registro de Acessos */}
        {activeTab === 'acessos' && (
          <>
            {/* Filtros */}
            <div className="card mb-4">
              <div className="card-header">
                <h5><i className="fas fa-filter me-2"></i>Filtros</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Filtrar por usuário ou nome..."
                      value={filtros.username}
                      onChange={(e) => setFiltros(prev => ({...prev, username: e.target.value}))}
                    />
                  </div>
                  <div className="col-md-4">
                    <select
                      className="form-select"
                      value={filtros.status}
                      onChange={(e) => setFiltros(prev => ({...prev, status: e.target.value}))}
                    >
                      <option value="">Todos os Status</option>
                      <option value="ativo">Sessões Ativas</option>
                      <option value="finalizado">Sessões Finalizadas</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <button 
                      className="btn btn-outline-secondary w-100"
                      onClick={() => setFiltros({username: '', status: ''})}
                    >
                      <i className="fas fa-eraser me-1"></i>
                      Limpar
                    </button>
                  </div>
                </div>
                
                <div className="mt-3 text-center">
                  <small className="text-muted">
                    Exibindo {acessosFiltrados.length} de {acessos.length} registros
                  </small>
                </div>
              </div>
            </div>

            {/* Tabela de Acessos */}
            <div className="card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>Usuário</th>
                        <th>Nome</th>
                        <th>Login</th>
                        <th>Logout</th>
                        <th>Duração</th>
                        <th>IP</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {acessosFiltrados.map((acesso, index) => (
                        <tr key={index}>
                          <td>
                            <FaUser className="me-2 text-primary" />
                            <strong>{acesso.username}</strong>
                          </td>
                          <td>{acesso.first_name}</td>
                          <td>
                            <FaSignInAlt className="me-2 text-success" />
                            {acesso.login_time_formatted}
                          </td>
                          <td>
                            <FaSignOutAlt className={`me-2 ${acesso.logout_time ? 'text-danger' : 'text-warning'}`} />
                            {acesso.logout_time_formatted}
                          </td>
                          <td>
                            <FaClock className="me-2" />
                            {acesso.session_duration_formatted}
                          </td>
                          <td>
                            <FaNetworkWired className="me-2" />
                            {acesso.ip_address}
                          </td>
                          <td>
                            <span className={`badge bg-${acesso.status_class}`}>
                              {acesso.status === 'ativa' ? 'Online' : 'Offline'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {acessosFiltrados.length === 0 && (
                  <div className="text-center p-4">
                    <i className="fas fa-search fa-3x text-muted mb-3"></i>
                    <p className="text-muted">Nenhum registro encontrado com os filtros aplicados.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Tab Usuários Online */}
        {activeTab === 'online' && (
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5>
                <FaEye className="me-2" />
                Usuários Online Agora
              </h5>
              <button 
                className="btn btn-outline-success btn-sm"
                onClick={carregarUsuariosOnline}
              >
                <i className="fas fa-sync-alt me-2"></i>
                Atualizar
              </button>
            </div>
            <div className="card-body">
              {usuariosOnline.length > 0 ? (
                <div className="row g-3">
                  {usuariosOnline.map((usuario, index) => (
                    <div key={index} className="col-md-6 col-lg-4">
                      <div className="card border-success">
                        <div className="card-body text-center">
                          <FaUser className="fa-2x text-success mb-2" />
                          <h6 className="card-title">{usuario.username}</h6>
                          <p className="card-text small text-muted">{usuario.first_name}</p>
                          <div className="d-flex justify-content-between small">
                            <span><FaClock className="me-1" />{usuario.tempo_online}</span>
                            <span><FaNetworkWired className="me-1" />{usuario.ip_address}</span>
                          </div>
                          <div className="mt-2">
                            <span className="badge bg-success">Online</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-4">
                  <FaUsers className="fa-3x text-muted mb-3" />
                  <p className="text-muted">Nenhum usuário online no momento.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab Estatísticas */}
        {activeTab === 'estatisticas' && (
          <div className="row g-4">
            <div className="col-md-6 col-lg-3">
              <div className="card text-center border-primary">
                <div className="card-body">
                  <FaUsers className="fa-2x text-primary mb-2" />
                  <h5 className="card-title text-primary">{estatisticas.usuarios_hoje || 0}</h5>
                  <p className="card-text small">Usuários Únicos Hoje</p>
                </div>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="card text-center border-success">
                <div className="card-body">
                  <FaEye className="fa-2x text-success mb-2" />
                  <h5 className="card-title text-success">{estatisticas.sessoes_ativas || 0}</h5>
                  <p className="card-text small">Sessões Ativas</p>
                </div>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="card text-center border-info">
                <div className="card-body">
                  <FaSignInAlt className="fa-2x text-info mb-2" />
                  <h5 className="card-title text-info">{estatisticas.acessos_hoje || 0}</h5>
                  <p className="card-text small">Acessos Hoje</p>
                </div>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="card text-center border-warning">
                <div className="card-body">
                  <FaClock className="fa-2x text-warning mb-2" />
                  <h5 className="card-title text-warning">{estatisticas.tempo_medio_sessao || 'N/A'}</h5>
                  <p className="card-text small">Tempo Médio de Sessão</p>
                </div>
              </div>
            </div>
            
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5><FaChartLine className="me-2" />Informações Adicionais</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <p><strong>Usuário Mais Ativo (7 dias):</strong></p>
                      <span className="badge bg-primary fs-6">{estatisticas.usuario_mais_ativo || 'N/A'}</span>
                    </div>
                    <div className="col-md-6">
                      <p><strong>Última Atualização:</strong></p>
                      <span className="text-muted">{new Date().toLocaleString('pt-BR')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegistroAcessos;